import { useCallback, useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { BrandLogo } from '../components/BrandLogo';
import { listPublicSubscriptionPlans, type SubscriptionPlan } from '../lib/api';
import { useAuth } from '../lib/auth';

const PRICING_ENQUIRY_HREF = 'mailto:support@yourl.co.za?subject=Storyly%20pricing%20enquiry';
const FALLBACK_PLANS: SubscriptionPlan[] = [
  { planId: 'free', name: 'Free', description: 'A simple way to start sharing client galleries.', public: true, sortOrder: 0, pricingMode: 'free', monthlyPriceCents: 0, annualPriceCents: 0, limits: { storageBytes: 3_000_000_000, activeGalleries: 3, photosPerGallery: 500 } },
  { planId: 'essential', name: 'Essential', description: 'For growing photographers delivering regularly.', public: true, sortOrder: 1, pricingMode: 'fixed', monthlyPriceCents: 14_900, annualPriceCents: 149_000, limits: { storageBytes: 50_000_000_000, activeGalleries: 25, photosPerGallery: 1_000 } },
  { planId: 'studio', name: 'Studio', description: 'For established studios with larger archives.', public: true, sortOrder: 2, pricingMode: 'fixed', monthlyPriceCents: 34_900, annualPriceCents: 349_000, limits: { storageBytes: 250_000_000_000, photosPerGallery: 2_000 } },
  { planId: 'enterprise', name: 'Enterprise', description: 'Custom capacity and service for high-volume teams.', public: true, sortOrder: 3, pricingMode: 'quote', minimumStorageBytes: 500_000_000_000, limits: { storageBytes: 500_000_000_000, photosPerGallery: 2_000 } }
];

const featureItems = [
  {
    number: '01',
    title: 'Fast, local delivery',
    copy: 'Locally hosted technology keeps every upload and client handover moving.'
  },
  {
    number: '02',
    title: 'Secure, private galleries',
    copy: 'Your work stays protected and is shared only with the people it belongs to.'
  },
  {
    number: '03',
    title: 'Beautiful client experiences',
    copy: 'Thoughtful galleries let the photographs lead, on every screen.'
  },
  {
    number: '04',
    title: 'Personal support',
    copy: 'Real help from people who understand photography and the work behind it.'
  }
];

export function LandingPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const pageRef = useRef<HTMLDivElement>(null);
  const collageRef = useRef<HTMLDivElement>(null);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [billing, setBilling] = useState<'monthly' | 'annual'>('monthly');
  const plansQuery = useQuery({ queryKey: ['public-subscription-plans'], queryFn: listPublicSubscriptionPlans });
  const plans = plansQuery.data?.plans ?? FALLBACK_PLANS;

  useLandingReveals(pageRef);
  useHeroParallax(collageRef);

  const handleSignIn = useCallback(async () => {
    if (isSigningIn) return;
    setAuthError(null);
    setIsSigningIn(true);
    try {
      await login();
    } catch {
      setAuthError('We could not start sign in. Please try again.');
      setIsSigningIn(false);
    }
  }, [isSigningIn, login]);

  const handleSignUp = useCallback(() => {
    if (!isSigningIn) navigate('/signup');
  }, [isSigningIn, navigate]);

  return (
    <div ref={pageRef} className="landing-page">
      <a className="landing-skip-link" href="#landing-content">Skip to content</a>

      <header className="landing-header">
        <a className="landing-brand-link" href="#top" aria-label="Storyly home">
          <BrandLogo variant="wordmark" className="landing-wordmark" />
        </a>
        <nav className="landing-navigation" aria-label="Main navigation">
          <div className="landing-section-links">
            <a href="#why-storyly">Why Storyly</a>
            <a href="#platform">Platform</a>
            <a href="#experience">Experience</a>
            <a href="#pricing">Pricing</a>
          </div>
          <SignInButton className="landing-nav-signin" isLoading={isSigningIn} onClick={handleSignIn} />
        </nav>
      </header>

      <main id="landing-content">
        <section id="top" className="landing-hero" aria-labelledby="landing-title">
          <div className="landing-hero-copy">
            <p className="landing-eyebrow landing-hero-eyebrow">Proudly South African. Built by photographers.</p>
            <h1 id="landing-title">Built for every <span>story.</span></h1>
            <p className="landing-hero-intro">
              A fast, intuitive gallery platform that carries your work beautifully from first upload to final delivery.
            </p>
            <div className="landing-hero-actions">
              <SignInButton isLoading={isSigningIn} onClick={handleSignIn} />
              <a className="landing-button landing-button-secondary" href="#pricing">
                View pricing
              </a>
            </div>
            {authError ? <p id="landing-auth-error" className="landing-auth-error" role="alert">{authError}</p> : null}
          </div>

          <div ref={collageRef} className="landing-collage" aria-label="Photography and editing in practice">
            <figure className="landing-collage-item landing-collage-photographer">
              <img
                src="/brand/landing/photographer-at-work-900.webp"
                srcSet="/brand/landing/photographer-at-work-480.webp 480w, /brand/landing/photographer-at-work-900.webp 900w"
                sizes="(min-width: 1024px) 24vw, 48vw"
                width="900"
                height="1350"
                alt="Photographer working beside the ocean"
                fetchPriority="high"
              />
            </figure>
            <figure className="landing-collage-item landing-collage-bride">
              <img
                src="/brand/landing/monochrome-bride-832.webp"
                srcSet="/brand/landing/monochrome-bride-480.webp 480w, /brand/landing/monochrome-bride-832.webp 832w"
                sizes="(min-width: 1024px) 15vw, 38vw"
                width="832"
                height="1248"
                alt="Bride in a bow-backed gown photographed in monochrome"
              />
            </figure>
            <figure className="landing-collage-item landing-collage-veil">
              <img
                src="/brand/landing/warm-veil-portrait-797.webp"
                srcSet="/brand/landing/warm-veil-portrait-480.webp 480w, /brand/landing/warm-veil-portrait-797.webp 797w"
                sizes="(min-width: 1024px) 14vw, 35vw"
                width="797"
                height="1235"
                alt="Warm bridal portrait seen through a veil"
              />
            </figure>
            <figure className="landing-collage-item landing-collage-workspace">
              <img
                src="/brand/landing/editing-workspace-1200.webp"
                srcSet="/brand/landing/editing-workspace-640.webp 640w, /brand/landing/editing-workspace-1200.webp 1200w"
                sizes="(min-width: 1024px) 22vw, 50vw"
                width="1200"
                height="801"
                alt="Camera and photograph-editing workspace"
              />
            </figure>
          </div>
        </section>

        <section id="why-storyly" className="landing-values" aria-label="Storyly values">
          <p data-reveal>Local</p>
          <span aria-hidden="true">·</span>
          <p data-reveal>Simplicity</p>
          <span aria-hidden="true">·</span>
          <p data-reveal>Performance</p>
          <span aria-hidden="true">·</span>
          <p data-reveal>Connection</p>
        </section>

        <section id="platform" className="landing-confidence">
          <div className="landing-confidence-copy" data-reveal>
            <p className="landing-eyebrow">From upload to delivery</p>
            <h2>More than storage. <span>Confidence.</span></h2>
            <p>
              Storyly gives every photographer a calm, capable place to deliver their work. Upload quickly, keep every gallery secure, and share a client experience that feels considered from beginning to end.
            </p>
            <a className="landing-text-link" href="#experience">See what matters <span aria-hidden="true">↘</span></a>
          </div>
          <div className="landing-product-frame" data-reveal>
            <img
              src="/brand/landing/storyly-dashboard-mockup-1440.webp"
              srcSet="/brand/landing/storyly-dashboard-mockup-768.webp 768w, /brand/landing/storyly-dashboard-mockup-1440.webp 1440w"
              sizes="(min-width: 1024px) 56vw, 92vw"
              width="1440"
              height="960"
              alt="Storyly photographer dashboard shown on a laptop"
              loading="lazy"
            />
          </div>
        </section>

        <section id="experience" className="landing-experience">
          <div className="landing-section-heading" data-reveal>
            <p className="landing-eyebrow">Share beautifully</p>
            <h2>Everything your stories need. Nothing they do not.</h2>
          </div>
          <div className="landing-feature-grid">
            {featureItems.map((feature) => (
              <article key={feature.number} className="landing-feature-card" data-reveal>
                <span>{feature.number}</span>
                <h3>{feature.title}</h3>
                <p>{feature.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-local">
          <p className="landing-eyebrow" data-reveal>Made here, for here</p>
          <div className="landing-local-grid">
            <h2 data-reveal>South African by design. Photographer-built by nature.</h2>
            <div data-reveal>
              <p>
                Storyly was created by people who understand the pace, care, and trust behind professional photography. Local infrastructure keeps delivery close; personal support keeps it human.
              </p>
              <p className="landing-local-note">Your work deserves a home that understands how it was made.</p>
            </div>
          </div>
        </section>

        <section id="pricing" className="landing-pricing" aria-labelledby="landing-pricing-title">
          <div className="landing-section-heading" data-reveal>
            <p className="landing-eyebrow">Simple South African pricing</p>
            <h2 id="landing-pricing-title">A plan for every stage of your studio.</h2>
            <p>All plans include beautiful galleries, client proofing, watermarking, video, and unlimited client downloads subject to fair use.</p>
          </div>
          <div className="landing-pricing-toggle" role="group" aria-label="Billing period" data-reveal>
            <button type="button" aria-pressed={billing === 'monthly'} onClick={() => setBilling('monthly')}>Monthly</button>
            <button type="button" aria-pressed={billing === 'annual'} onClick={() => setBilling('annual')}>Annual · two months free</button>
          </div>
          <div className="landing-pricing-grid">
            {plans.map((plan) => (
              <PricingCard key={plan.planId} plan={plan} billing={billing} busy={isSigningIn} onSignUp={handleSignUp} />
            ))}
          </div>
        </section>

        <section className="landing-closing" aria-labelledby="landing-closing-title">
          <div data-reveal>
            <p className="landing-eyebrow">Your next story starts here</p>
            <h2 id="landing-closing-title">Ready to share your work beautifully?</h2>
            <p>Talk to us about pricing, or sign in to continue to your studio.</p>
          </div>
          <div className="landing-closing-actions" data-reveal>
            <a className="landing-button landing-button-light" href="#pricing">View pricing</a>
            <SignInButton className="landing-button-outline" isLoading={isSigningIn} onClick={handleSignIn} />
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <BrandLogo variant="wordmark" className="landing-footer-wordmark" />
        <p>Built locally for South African photographers.</p>
        <a href="mailto:support@yourl.co.za">support@yourl.co.za</a>
      </footer>
    </div>
  );
}

function PricingCard({
  plan,
  billing,
  busy,
  onSignUp
}: {
  plan: SubscriptionPlan;
  billing: 'monthly' | 'annual';
  busy: boolean;
  onSignUp(): void;
}) {
  const annual = billing === 'annual';
  const price = plan.pricingMode === 'quote'
    ? 'Custom quote'
    : plan.pricingMode === 'free'
      ? 'R0'
      : formatRand(annual ? plan.annualPriceCents : plan.monthlyPriceCents);
  const period = plan.pricingMode === 'fixed' ? (annual ? '/ year' : '/ month') : '';
  return (
    <article className={`landing-pricing-card ${plan.planId === 'studio' ? 'landing-pricing-card-featured' : ''}`} data-reveal>
      {plan.planId === 'studio' ? <span className="landing-pricing-popular">Most popular</span> : null}
      <h3>{plan.name}</h3>
      <p className="landing-pricing-description">{plan.description}</p>
      <p className="landing-pricing-price"><strong>{price}</strong> <span>{period}</span></p>
      <ul>
        <li>{formatStorage(plan.limits.storageBytes ?? plan.minimumStorageBytes)} storage{plan.planId === 'enterprise' ? '+' : ''}</li>
        <li>{plan.limits.activeGalleries ? `${plan.limits.activeGalleries} active galleries` : plan.planId === 'enterprise' ? 'Custom gallery capacity' : 'Unlimited active galleries'}</li>
        <li>Up to {plan.limits.photosPerGallery.toLocaleString('en-ZA')} photos per gallery</li>
        <li>Unlimited client downloads</li>
      </ul>
      {plan.planId === 'enterprise' ? (
        <a className="landing-button landing-button-secondary" href={PRICING_ENQUIRY_HREF}>Contact Storyly</a>
      ) : (
        <button type="button" className="landing-button landing-button-primary" disabled={busy} onClick={onSignUp}>
          {busy ? 'Opening…' : plan.planId === 'free' ? 'Create free account' : 'Start free, then upgrade'}
        </button>
      )}
    </article>
  );
}

function formatRand(cents?: number): string {
  return `R${Math.round((cents ?? 0) / 100).toLocaleString('en-ZA')}`;
}

function formatStorage(bytes?: number): string {
  if (!bytes) return 'Custom';
  return `${Math.round(bytes / 1_000_000_000).toLocaleString('en-ZA')} GB`;
}

function SignInButton({
  className = '',
  isLoading,
  onClick
}: {
  className?: string;
  isLoading: boolean;
  onClick(): void;
}) {
  return (
    <button
      type="button"
      className={`landing-button landing-button-primary ${className}`.trim()}
      disabled={isLoading}
      aria-busy={isLoading}
      aria-describedby="landing-auth-error"
      onClick={onClick}
    >
      {isLoading ? 'Opening sign in…' : 'Sign In'}
    </button>
  );
}

function useLandingReveals(pageRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    const elements = Array.from(root.querySelectorAll<HTMLElement>('[data-reveal]'));
    const reduceMotion = typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion || typeof IntersectionObserver === 'undefined') {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.12 }
    );
    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [pageRef]);
}

function useHeroParallax(collageRef: React.RefObject<HTMLDivElement | null>) {
  useEffect(() => {
    const collage = collageRef.current;
    if (!collage || typeof window.matchMedia !== 'function') return;
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let frameId = 0;

    const update = () => {
      frameId = 0;
      if (reduceMotion.matches || window.innerWidth < 1024) {
        collage.style.setProperty('--landing-parallax-near', '0px');
        collage.style.setProperty('--landing-parallax-far', '0px');
        collage.style.setProperty('--landing-parallax-reverse', '0px');
        return;
      }
      const rect = collage.getBoundingClientRect();
      const viewportCentre = window.innerHeight / 2;
      const collageCentre = rect.top + rect.height / 2;
      const progress = Math.max(-1, Math.min(1, (viewportCentre - collageCentre) / window.innerHeight));
      collage.style.setProperty('--landing-parallax-near', `${progress * 24}px`);
      collage.style.setProperty('--landing-parallax-far', `${progress * 13}px`);
      collage.style.setProperty('--landing-parallax-reverse', `${progress * -10}px`);
    };

    const scheduleUpdate = () => {
      if (frameId) return;
      frameId = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', scheduleUpdate, { passive: true });
    window.addEventListener('resize', scheduleUpdate);
    reduceMotion.addEventListener?.('change', scheduleUpdate);
    return () => {
      window.removeEventListener('scroll', scheduleUpdate);
      window.removeEventListener('resize', scheduleUpdate);
      reduceMotion.removeEventListener?.('change', scheduleUpdate);
      if (frameId) window.cancelAnimationFrame(frameId);
    };
  }, [collageRef]);
}
